import { useState, useEffect, useCallback, useRef } from "react"
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from "react-router-dom"
import { externalEntityService } from "../../services/externalEntityService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader"
import Alert from "../common/Alert"
import { Camera } from "lucide-react"
import ListFilterPanel from '../common/ListFilterPanel.js'
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js'
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js'
import ListPagination from '../common/ListPagination.js'
import SearchSubmitInput from '../common/SearchSubmitInput.js'
import { useTranslation } from 'react-i18next';


const ExternalMLList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const fileInputRef = useRef(null)

  const [externalMLs, setExternalMLs] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [filters, setFilters] = useState({ search: "" })
  const [openMenuId, setOpenMenuId] = useState(null)
  const [uploadFile, setUploadFile] = useState(null)
  const [uploading, setUploading] = useState(false)

  const fetchExternalMLs = useCallback(async () => {
    try {
      setLoading(true)
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "")
      )
      const response = await externalEntityService.getAllExternalML(currentPage, 10, cleanFilters)
      const data = response.data
      const rows = Array.isArray(data.ml_sheets) ? data.ml_sheets : []

      setExternalMLs((prev) => mergeLoadedPage(currentPage, rows, prev))
      setTotalPages(data.pagination?.total_pages || 1)
    } catch (error) {
      console.error("Error fetching External MLs:", error)
      Alert.error(error.response?.data?.message || "Failed to load External ML Sheets")
      if (currentPage <= 1) setExternalMLs([])
    } finally {
      setLoading(false)
    }
  }, [filters, currentPage])

  usePaginatedListFetch({
    fetchPage: fetchExternalMLs,
    setCurrentPage,
    currentPage,
    resetPageDeps: [filters.search, filters.from_location_id, filters.to_location_id, filters.vehicle_id],
    debounceWhen: filters.search,
    debounceMs: 0,
  })

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId])

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  const handleClearFilters = () => {
    setFilters({ search: "" })
  }

  const hasActiveFilters = filters.search || filters.from_location_id || filters.to_location_id || filters.vehicle_id

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0] || null)
  }

  const handleUpload = async () => {
    if (!uploadFile) {
      Alert.error("Please select a PDF file to upload")
      return
    }
    if (!uploadFile.name.toLowerCase().endsWith(".pdf")) {
      Alert.error("Only PDF files are accepted")
      return
    }
    try {
      setUploading(true)
      // uploadMLSheetPDF(pdfFile, userId) → POST /api/external-entities/upload-ml-sheet
      await externalEntityService.uploadMLSheetPDF(uploadFile, user?.user_id)
      Alert.success("PDF uploaded — processing in background. Refresh in a moment.")
      setUploadFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
      // Give the background processor a couple seconds then reload
      setTimeout(() => fetchExternalMLs(), 3000)
    } catch (error) {
      console.error("Error uploading PDF:", error)
      Alert.error(error.response?.data?.message || "Failed to upload ML Sheet PDF")
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (externalMLId) => {
    if (!window.confirm("Are you sure you want to delete this External ML Sheet?")) return
    try {
      await externalEntityService.deleteExternalML(externalMLId)
      Alert.success("External ML Sheet deleted successfully")
      setOpenMenuId(null)
      fetchExternalMLs()
    } catch (error) {
      console.error("Error deleting External ML Sheet:", error)
      Alert.error(error.response?.data?.message || "Failed to delete External ML Sheet")
    }
  }

  const canEdit = () =>
    ["System Admin", "Admin", "Accountant"].includes(user?.role)

  const canDelete = () =>
    ["System Admin", "Admin"].includes(user?.role)

  return (
    <DashboardLayout>

                <ListPageHeader
                  title="External ML Sheets"
                  titleClassName="text-secondary"
                  className="mb-4 sm:mb-6"
                />

                {/* Upload Section */}
                {canEdit() && (
                  <div className="bg-white rounded-xl p-4 sm:p-6 mb-6 border border-gray-200">
                    <h3 className="text-sm font-semibold text-secondary mb-3">
                      Upload Trip Sheet PDF
                    </h3>
                    <div className="flex flex-col sm:flex-row items-start gap-4">

  {/* Hidden input */}
  <input
    ref={fileInputRef}
    type="file"
    accept=".pdf"
    onChange={handleFileChange}
    className="hidden"
    id="ml-upload"
  />

  {/* Upload Box */}
  <label
    htmlFor="ml-upload"
    className="w-44 h-44 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center relative bg-gray-50 hover:bg-gray-100 transition cursor-pointer"
  >
    {!uploadFile ? (
      <div className="text-center">
        <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
        <p className="text-xs text-gray-500">Click to upload</p>
      </div>
    ) : (
      <div className="flex flex-col items-center justify-center text-center px-2">
        <Camera className="w-8 h-8 text-gray-500 mb-2" />
        <p className="text-xs text-gray-600 break-all">
          {uploadFile.name}
        </p>
      </div>
    )}

    {uploadFile && (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          setUploadFile(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
        }}
        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
      >
        ✕
      </button>
    )}
  </label>

  {/* Upload Button */}
  <Button variant="primary" onClick={handleUpload} disabled={uploading || !uploadFile}>
    {uploading ? "Uploading..." : "Upload PDF"}
  </Button>

</div>
</div>
)}

                <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters}>
                  <div>
                    <SearchSubmitInput
                      name="search"
                      value={filters.search}
                      onSearch={(value) => setFilters((prev) => ({ ...prev, search: value }))}
                      placeholder="Search by ML ID, Trip Sheet No, Driver..."
                      inputClassName="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      From Location ID
                    </label>
                    <input
                      type="number"
                      name="from_location_id"
                      value={filters.from_location_id || ""}
                      onChange={handleFilterChange}
                      placeholder="From location ID"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      To Location ID
                    </label>
                    <input
                      type="number"
                      name="to_location_id"
                      value={filters.to_location_id || ""}
                      onChange={handleFilterChange}
                      placeholder="To location ID"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                      Vehicle ID
                    </label>
                    <input
                      type="number"
                      name="vehicle_id"
                      value={filters.vehicle_id || ""}
                      onChange={handleFilterChange}
                      placeholder="Vehicle ID"
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-blue-400"
                    />
                  </div>
                </ListFilterPanel>

                {/* Table */}
                <div className="bg-white rounded-xl shadow-sm">
                  {loading && currentPage === 1 ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader fullScreen={false} size="default" message="Loading External ML Sheets..." />
                    </div>
                  ) : externalMLs.length === 0 ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500 text-lg font-semibold">No External ML Sheets found</p>
                    </div>
                  ) : (
                    <>
                      {/* Desktop Table */}
                      <div className="hidden lg:block w-full overflow-x-auto">
                        <table className="w-full table-fixed border-collapse">
                          <thead>
                            <tr className="bg-primary text-tertiary">
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold w-[6%] text-center">Sr No.</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold w-[18%] text-center">ML ID</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold text-center">Trip Sheet No</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold text-center">Driver</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold text-center">{t('pages.shipment.title')}</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold text-center">Total To Pay</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold text-center">Created</th>
                              <th className="px-4 py-3 text-xs lg:text-sm font-semibold text-center">{t('common.actions')}</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {externalMLs.map((ml, index) => (
                              <tr
                                key={ml.external_ml_id}
                                className={`hover:bg-gray-50 text-center transition-colors ${
                                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                                }`}
                              >
                                <td className="px-5 py-3 text-sm">
                                  {index + 1}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <span
                                    onClick={() => navigate(`/external-ml/view/${ml.external_ml_id}`)}
                                    className="text-secondary font-regular cursor-pointer text-sm hover:text-blue-600 hover:underline"
                                  >
                                    {ml.external_ml_id}
                                  </span>
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">
                                  {ml.trip_sheet_no || "—"}
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">
                                  {ml.driver_name
                                    ? `${ml.driver_name}${ml.driver_contact ? ` (${ml.driver_contact})` : ""}`
                                    : "—"}
                                </td>
                                <td className="px-5 py-3 text-sm text-center">
                                  {ml.external_shipment_count || 0}
                                </td>
                                <td className="px-5 py-3 text-sm text-center font-semibold">
                                  ₹{(ml.total_topay || 0).toFixed(2)}
                                </td>
                                <td className="px-5 py-3 text-sm text-gray-600 text-center">
                                  {ml.created_at
                                    ? new Date(ml.created_at).toLocaleDateString("en-IN")
                                    : "—"}
                                </td>
                                <td className="px-5 py-3 text-center">
                                  <RowActionsMenu
                                    className="relative menu-container"
                                    isOpen={openMenuId === ml.external_ml_id}
                                    onToggle={() => setOpenMenuId(openMenuId === ml.external_ml_id ? null : ml.external_ml_id)}
                                    onClose={() => setOpenMenuId(null)}
                                    onView={() => navigate(`/external-ml/view/${ml.external_ml_id}`)}
                                    onDelete={canDelete() ? () => handleDelete(ml.external_ml_id) : undefined}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Cards */}
                      <div className="lg:hidden divide-y divide-gray-200">
                        {externalMLs.map((ml) => (
                          <div key={ml.external_ml_id} className="p-4 hover:bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                              <span
                                onClick={() => navigate(`/external-ml/view/${ml.external_ml_id}`)}
                                className="text-secondary font-semibold cursor-pointer hover:text-blue-600 hover:underline text-sm"
                              >
                                {ml.external_ml_id}
                              </span>
                              <RowActionsMenu
                                className="relative menu-container"
                                isOpen={openMenuId === ml.external_ml_id}
                                onToggle={() => setOpenMenuId(openMenuId === ml.external_ml_id ? null : ml.external_ml_id)}
                                onClose={() => setOpenMenuId(null)}
                                onView={() => navigate(`/external-ml/view/${ml.external_ml_id}`)}
                                onDelete={canDelete() ? () => handleDelete(ml.external_ml_id) : undefined}
                              />
                            </div>
                            <div className="text-xs text-gray-600 space-y-1">
                              <div>Trip Sheet: {ml.trip_sheet_no || "—"}</div>
                              <div>Driver: {ml.driver_name || "—"}</div>
                              <div>Shipments: {ml.external_shipment_count || 0}</div>
                              <div>Total To Pay: ₹{(ml.total_topay || 0).toFixed(2)}</div>
                              <div>
                                Created:{" "}
                                {ml.created_at
                                  ? new Date(ml.created_at).toLocaleDateString("en-IN")
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        loading={loading}
                        onNext={() => setCurrentPage((page) => page + 1)}
                      />
                    </>
                  )}
                </div>

    </DashboardLayout>
  )
}

export default ExternalMLList